import { useEffect, useState, useMemo } from "react";
import { loadJsonData, getCurrentSeason } from "../../utils/hybridDataLoader";
import { apiUrl } from "../../../../utils/apiConfig";
import { loadSmartFantasyDataFromJson } from "./smartFantasyFallback";

// Global cache for smart fantasy data
const smartFantasyCache = new Map();

/**
 * Smart fantasy data hook that loads exactly what we need:
 * - Only drivers in the selected group
 * - Only data from 2022+ (user requirement)
 * - Massive reduction from 60k+ to ~8k records maximum
 * - Organizes data by track categories and recent form
 */
export const useSmartFantasyData = (selectedGroup, useStarGroup) => {
  const [state, setState] = useState({
    trackRaces: [],
    similarTrackRaces: [],
    allRaces: [],
    currentSeasonRaces: [],
    pastSeasonRaces: [],
    last5Races: [],
    last10Races: [],
    groupDrivers: [],
    isLoading: false,
    error: null,
    metadata: {}
  });
  
  const [staticData, setStaticData] = useState({
    nextRaceData: {},
    isLoaded: false
  });

  // Load static data once
  useEffect(() => {
    const loadStaticData = async () => {
      try {
        const nextRaceData = await loadJsonData("next_race_data.json");
        
        setStaticData({
          nextRaceData,
          isLoaded: true
        });
      } catch (error) {
        console.error("Error loading static data:", error);
        setState(prev => ({ ...prev, error: error.message }));
      }
    };

    loadStaticData();
  }, []);

  // Create cache key
  const cacheKey = useMemo(() => {
    if (!staticData.isLoaded) return null;
    const groupKey = Array.isArray(selectedGroup) ? selectedGroup.join(',') : selectedGroup;
    return `smart_${groupKey}_${useStarGroup}_${staticData.nextRaceData.next_race_season}`;
  }, [selectedGroup, useStarGroup, staticData.isLoaded, staticData.nextRaceData.next_race_season]);

  useEffect(() => {
    if (!selectedGroup || !staticData.isLoaded || !cacheKey) return;

    // Check cache first
    if (smartFantasyCache.has(cacheKey)) {
      setState(prev => ({ ...prev, ...smartFantasyCache.get(cacheKey), isLoading: false }));
      return;
    }

    // Ignore responses that arrive after the selection has changed
    let cancelled = false;

    const loadSmartData = async () => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      try {
        const { nextRaceData } = staticData;
        const nextRaceTrack = nextRaceData.next_race_track;
        // The season the data is in; it also holds between the last race of a year and the next calendar, when
        // next_race_data names no season at all
        const currentSeason = await getCurrentSeason();

        // Validate group selection for current mode
        const { starGroups, openGroups } = await import("./fantasyGroups");
        const validGroups = useStarGroup ? starGroups : openGroups;
        
        const selectedGroupsArray = Array.isArray(selectedGroup) ? selectedGroup : [selectedGroup];
        
        // Ensure all selected groups are valid
        const hasInvalidGroup = !selectedGroupsArray.includes("All") && selectedGroupsArray.some(g => !validGroups.includes(g));
        if (hasInvalidGroup) {
          console.warn(`Invalid group selection for ${useStarGroup ? 'star' : 'open'} mode`);
          if (!cancelled) setState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        // The server resolves the group's drivers, so one request returns everything
        const groupType = useStarGroup ? "star" : "open";
        let smartData;
        try {
          const response = await fetch(
            apiUrl(`/smart-fantasy/race-data?groupType=${groupType}&groups=${encodeURIComponent(selectedGroupsArray.join(','))}&track=${encodeURIComponent(nextRaceTrack)}&season=${currentSeason}&seasons=3&includeForm=true&includeSimilar=true`)
          );

          if (!response.ok) {
            throw new Error(`Smart fantasy API failed: ${response.status} ${response.statusText}`);
          }

          smartData = await response.json();
        } catch (apiError) {
          console.warn("Smart fantasy API unavailable, building the data from JSON files:", apiError.message);
          smartData = await loadSmartFantasyDataFromJson({
            groupType,
            groups: selectedGroupsArray,
            track: nextRaceTrack,
            season: currentSeason
          });
        }
        if (cancelled) return;

        if (!smartData.groupDrivers || smartData.groupDrivers.length === 0) {
          // If no drivers found, this might be an invalid group combination
          // (e.g., star group "II" with open_group filter)
          // Let the FantasyMainScreen handle the group reset
          console.warn(`No drivers found for groups ${selectedGroupsArray.join(',')} in ${groupType}_group, likely invalid combination`);
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        const newState = {
          trackRaces: smartData.trackRaces || [],
          similarTrackRaces: smartData.similarTrackRaces || [],
          allRaces: smartData.allRaces || [],
          currentSeasonRaces: smartData.currentSeasonRaces || [],
          pastSeasonRaces: smartData.pastSeasonRaces || [],
          last5Races: smartData.last5Races || [],
          last10Races: smartData.last10Races || [],
          groupDrivers: smartData.groupDrivers,
          isLoading: false,
          error: null,
          metadata: smartData.metadata || {}
        };

        // Cache the result
        smartFantasyCache.set(cacheKey, newState);
        setState(prev => ({ ...prev, ...newState }));
        
      } catch (error) {
        console.error("Error loading smart fantasy data:", error);
        if (cancelled) return;
        setState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: error.message 
        }));
      }
    };

    loadSmartData();
    return () => {
      cancelled = true;
    };
    // cacheKey encodes the group, the mode and the season, so it is the only value that should restart the load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return state;
};
